import { useState } from 'react';
import { Box, Typography, IconButton, TextField, Button } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { CCommentData } from '../hooks/dto/taskItem.dto';
import { colors } from '@src/styles/colors';
import { useAppSelector } from '@src/redux/hooks';
import { useTasksContext } from '../context/TasksContext';

interface CommentItemProps {
  comment: CCommentData;
}

export const CommentItem = ({ comment }: CommentItemProps) => {
  const { userNames, handleUpdateComment, handleDeleteComment } = useTasksContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentUser = useAppSelector((state) => state.authenticator.user);

  const isOwner = currentUser?.info?.id === comment.createdByUserId;
  const isEdited = comment.updatedAt !== comment.createdAt;
  const userName = userNames[comment.createdByUserId] || 'Usuário Desconhecido';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSave = async () => {
    if (editContent.trim() === '') return;
    setIsSubmitting(true);
    try {
      await handleUpdateComment(comment.id, editContent);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  const onDelete = async () => {
    if (!window.confirm('Deseja deletar este comentário?')) return;
    setIsSubmitting(true);
    try {
      await handleDeleteComment(comment.id);
    } catch (error) {
      console.error('Error deleting comment:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.background.secondary,
        border: `1px solid ${colors.background.secondary}`,
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <Box>
          <Typography variant="body2" sx={{ color: colors.primary.white, fontWeight: 600 }}>
            {userName}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.primary.gray }}>
            {formatDate(comment.createdAt)}
            {isEdited && ' (editado)'}
          </Typography>
        </Box>
        {isOwner && !isEditing && (
          <Box>
            <IconButton
              size="small"
              onClick={() => setIsEditing(true)}
              disabled={isSubmitting}
              sx={{ color: colors.primary.gray, '&:hover': { color: colors.primary.yellow } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={isSubmitting}
              sx={{ color: colors.primary.gray, '&:hover': { color: colors.primary.warning } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {isEditing ? (
        <Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            disabled={isSubmitting}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: colors.background.primary,
                color: colors.primary.white,
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleSave}
              disabled={isSubmitting || editContent.trim() === ''}
              sx={{
                backgroundColor: colors.primary,
                '&:hover': { backgroundColor: colors.primary.yellow },
              }}
            >
              Salvar
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{ borderColor: colors.primary.gray, color: colors.primary.gray }}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: colors.primary.white, whiteSpace: 'pre-wrap' }}>
          {comment.content}
        </Typography>
      )}
    </Box>
  );
};
